import type { OptionType, UserInterface } from '@/types'
import { denormalizePhoneForBackend } from '@/utils/phone/denormalizePhoneForBackend'

import type { UpdateUserRequest } from '../types/users.types'
import {
  getRoleLabel,
  sanitizeLogin,
  type UserEditFormType,
} from '../Users.config'

const LOGIN_COUNTRY_CODE = '375'
const LOCAL_PHONE_LENGTH = 9

export const normalizeLogin = (login: string) => {
  if (!login) return ''
  const digitsOnly = login.replace(/\D/g, '')
  return digitsOnly.length === LOCAL_PHONE_LENGTH
    ? LOGIN_COUNTRY_CODE + digitsOnly
    : login
}

export const normalizeLoginForPayload = (login = '') => {
  const processedLogin = denormalizePhoneForBackend(login)
  return processedLogin.length === LOCAL_PHONE_LENGTH
    ? LOGIN_COUNTRY_CODE + processedLogin
    : processedLogin
}

export const normalizeRoles = (roles?: string[]) =>
  Array.from(new Set(roles ?? [])).sort()

export const toUserEditFormValues = (
  user: UserInterface,
  roleOptions: OptionType[]
): UserEditFormType => ({
  description: user.description || '',
  login: normalizeLogin(user.login || '') || sanitizeLogin(''),
  phoneNumber: user.phoneNumber || '',
  email: user.email || '',
  enabled: user.enabled ?? true,
  roles: (user.roles || []).map((role) => ({
    value: role,
    label: getRoleLabel(role, roleOptions),
  })),
})

const rolesEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((role, index) => role === b[index])

export const isUserFormUnchanged = (
  values: Partial<UserEditFormType>,
  user: UserInterface
) =>
  (values.description || '') === (user.description || '') &&
  normalizeLoginForPayload(values.login || '') ===
    normalizeLoginForPayload(user.login || '') &&
  (values.phoneNumber || '') === (user.phoneNumber || '') &&
  (values.email || '') === (user.email || '') &&
  (values.enabled ?? true) === (user.enabled ?? true) &&
  rolesEqual(
    normalizeRoles((values.roles ?? []).map((role) => role.value)),
    normalizeRoles(user.roles)
  )

export const toUpdateUserRequest = (
  values: UserEditFormType,
  user: UserInterface,
  canManageUsers: boolean
): UpdateUserRequest => {
  const formRoles = normalizeRoles(values.roles.map((role) => role.value))
  return {
    name: user.name || values.description,
    description: values.description,
    email: values.email || '',
    phoneNumber: values.phoneNumber || '',
    login: normalizeLoginForPayload(values.login),
    enabled: canManageUsers ? values.enabled : user.enabled,
    roles: canManageUsers && formRoles.length ? formRoles : user.roles,
    telegramId: user.telegramId,
    telegramActivated: user.telegramActivated,
  }
}

export const hasRolesChanged = (
  payload: UpdateUserRequest,
  user: UserInterface
) => !rolesEqual(normalizeRoles(payload.roles), normalizeRoles(user.roles))
